import React, { useEffect, useState } from "react";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Users, Heart, Coins, Plus, Crown, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact team standings strip on /plyx/challenges/:id detail pages.
 * Lists teams sorted by total_gift_coins → total_hearts → entry_count.
 * Logged-in users can create a team or one-tap join an existing one.
 */
const COLORS = ["#FF2D7A", "#FFB42D", "#00E0C7", "#A45DFF", "#FF6B00", "#5BE34A"];

const TeamsStrip = ({ challengeId, isOpen, isLive }) => {
  const { user } = usePlyxAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", motto: "", color: COLORS[0] });

  const myTeamId = teams.find((t) => t.member_ids?.includes?.(user?.plyx_user_id))?.team_id;

  const load = () => {
    setLoading(true);
    plyxApi.get(`/plyx/challenges/${challengeId}/teams`)
      .then((r) => setTeams(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [challengeId]);

  const create = async (e) => {
    e?.preventDefault?.();
    if (form.name.trim().length < 2) { toast.error("Team name too short"); return; }
    setCreating(true);
    try {
      await plyxApi.post(`/plyx/challenges/${challengeId}/teams`, {
        name: form.name.trim(),
        motto: form.motto.trim() || null,
        color: form.color,
      });
      toast.success("Team created — invite your crew 🏆");
      setShowCreate(false);
      setForm({ name: "", motto: "", color: COLORS[0] });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't create team");
    } finally {
      setCreating(false);
    }
  };

  const join = async (teamId) => {
    try {
      await plyxApi.post(`/plyx/challenges/${challengeId}/teams/${teamId}/join`);
      toast.success("You're in. Now drop an entry video.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't join");
    }
  };

  if (loading) {
    return (
      <div className="mt-2 mb-8 flex items-center justify-center py-6 text-white/40 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }

  // Don't take up space if no teams and challenge is closed.
  if (teams.length === 0 && !isLive && !user) return null;

  return (
    <div className="mt-2 mb-8" data-testid="plyx-teams-strip">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display text-xl inline-flex items-center gap-2">
          <Crown className="w-5 h-5 text-[#FFB42D]" /> Team standings
          {teams.length > 0 && <span className="text-[11px] text-white/45 font-normal">· {teams.length}/32</span>}
        </h2>
        {user && isLive && (
          <button
            type="button"
            onClick={() => setShowCreate((v) => !v)}
            data-testid="plyx-teams-create-toggle"
            className="text-xs font-semibold px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center gap-1.5"
          >
            <Plus className="w-3 h-3" /> Form crew
          </button>
        )}
      </div>

      {showCreate && (
        <form onSubmit={create} className="rounded-2xl border border-white/10 bg-white/5 p-4 mb-4" data-testid="plyx-teams-create-form">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest font-bold text-[#FFB42D]">New crew</div>
            <button type="button" onClick={() => setShowCreate(false)} className="p-1 rounded-full hover:bg-white/10">
              <X className="w-4 h-4 text-white/60" />
            </button>
          </div>
          <input
            type="text"
            required
            minLength={2}
            maxLength={60}
            placeholder="Team name (e.g. Lagos Lions)"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="plyx-teams-create-name"
            className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FFB42D] mb-2 placeholder:text-white/40"
          />
          <input
            type="text"
            maxLength={140}
            placeholder="Motto (optional)"
            value={form.motto}
            onChange={(e) => setForm({ ...form, motto: e.target.value })}
            className="w-full rounded-xl bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FFB42D] mb-2 placeholder:text-white/40"
          />
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[11px] text-white/60">Color:</span>
            {COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setForm({ ...form, color: c })}
                className={`w-6 h-6 rounded-full border-2 ${form.color === c ? "border-white" : "border-transparent"}`}
                style={{ background: c }}
                aria-label={`Choose color ${c}`}
              />
            ))}
          </div>
          <button
            type="submit"
            disabled={creating}
            data-testid="plyx-teams-create-submit"
            className="w-full py-2.5 rounded-full font-bold text-white text-sm disabled:opacity-60"
            style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
          >
            {creating ? "Creating…" : "Create team"}
          </button>
        </form>
      )}

      {teams.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-6 text-center text-sm text-white/60">
          No crews yet — start one and bring your friends.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {teams.map((t, i) => {
            const isMine = t.team_id === myTeamId;
            return (
              <div
                key={t.team_id}
                data-testid={`plyx-team-card-${t.team_id}`}
                className={`relative rounded-2xl border p-4 transition-colors ${isMine ? "border-[#FFB42D]/60 bg-[#FFB42D]/10" : "border-white/10 bg-white/5"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: t.color || "#FFB42D" }} />
                  <div className="font-semibold flex-1 truncate">
                    {i === 0 && t.entry_count > 0 && <span className="mr-1">👑</span>}{t.name}
                  </div>
                  {isMine && (
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#FFB42D] text-[#0A0A0A]">MY CREW</span>
                  )}
                </div>
                {t.motto && <p className="text-xs text-white/65 mb-3 line-clamp-2">{t.motto}</p>}
                <div className="flex items-center gap-3 text-xs text-white/65">
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" /> {t.member_count}</span>
                  <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" /> {t.total_hearts}</span>
                  <span className="inline-flex items-center gap-1 text-[#FFB42D] font-semibold">
                    <Coins className="w-3 h-3" /> {t.total_gift_coins.toLocaleString()}
                  </span>
                </div>
                {!isMine && user && isLive && (
                  <button
                    type="button"
                    onClick={() => join(t.team_id)}
                    data-testid={`plyx-team-join-${t.team_id}`}
                    className="mt-3 w-full py-1.5 rounded-full text-xs font-bold text-[#0A0A0A]"
                    style={{ background: "linear-gradient(135deg, #FFB42D 0%, #FF2D7A 100%)" }}
                  >
                    Join crew
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TeamsStrip;
